<template>
  <section class="sidebar-card">
    <template v-if="can(Permissions.AnomalyCreate)">
      <header class="create-head">
        <span class="create-eyebrow">Place marker</span>
        <span v-if="!props.selectedPoint" class="create-hint">Pick a point first</span>
      </header>

      <div class="card-body action-grid">
        <button type="button" class="action-btn action-success" :disabled="props.selectedPoint === null"
          @click="openCreateAnomalyDialog">
          <v-icon size="19">mdi-plus-circle-outline</v-icon>
          <span>Anomaly</span>
        </button>

        <button type="button" class="action-btn action-info" :disabled="props.selectedPoint === null"
          @click="openCreateInspectionAreaDialog">
          <v-icon size="19">mdi-cube-outline</v-icon>
          <span>Zone</span>
        </button>

        <button type="button" class="action-btn action-danger" :disabled="props.selectedPoint === null"
          @click="openCreateHotSpotDialog">
          <v-icon size="19">mdi-fire</v-icon>
          <span>HotSpot</span>
        </button>
      </div>
    </template>

    <v-dialog v-model="createAnomalyDialog" max-width="400px">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">Create Anomaly</v-card-title>
        <v-card-text>
          <v-select v-model="selectedMarkerType" :items="markerTypes" item-title="label" item-value="id"
            return-object label="Select Marker Type" variant="outlined" density="comfortable" hide-details="auto" />
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn class="ghost-btn" @click="createAnomalyDialog = false">Cancel</v-btn>
          <v-btn class="primary-btn" @click="confirmCreateAnomaly">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="createHotSpotDialog" max-width="400px">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">Create HotSpot</v-card-title>
        <v-card-text>Create a new hotspot that identifies a collection of anomalies.</v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn class="ghost-btn" @click="createHotSpotDialog = false">Cancel</v-btn>
          <v-btn class="primary-btn" @click="confirmCreateHotSpot">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="createInspectionDialog" max-width="400px">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">Create Inspection Area</v-card-title>
        <v-card-text>
          <v-text-field v-model="newInspectionAreaName" label="Area Name" variant="outlined" density="comfortable"
            hide-details="auto" />
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn class="ghost-btn" @click="createInspectionDialog = false">Cancel</v-btn>
          <v-btn class="primary-btn" :disabled="!newInspectionAreaName" @click="confirmCreateInspectionArea">
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script setup>
import { defineEmits, defineExpose, defineOptions, defineProps, onMounted, ref } from 'vue';
import { Permissions } from '@/constants/permissions';
import { usePermissions } from '@/composable/usePermissions';
import MarkerService from '@/services/MarkerService';
import { useAssetStore } from '@/store/assetStore';

defineOptions({ name: "AnomalyActionsCard" })

const props = defineProps({
  selectedPoint: { type: Object, default: null }
})

const emits = defineEmits([
  'createMarker',
  'createInspectionArea',
  'onCreateHotSpot'
])

const { can } = usePermissions();
const asset = useAssetStore();

const createAnomalyDialog = ref(false)
const createInspectionDialog = ref(false)
const createHotSpotDialog = ref(false)
const selectedMarkerType = ref(null)
const newInspectionAreaName = ref(null)
const markerTypes = ref(null)

onMounted(async () => {
  newInspectionAreaName.value = ''

  if (can(Permissions.AnomalyCreate)) {
    markerTypes.value = await MarkerService.getMarkerTypesByOrganizationId(asset.asset.organizationId);
  }
})

const openCreateAnomalyDialog = () => { createAnomalyDialog.value = true }
const confirmCreateAnomaly = () => {
  emits('createMarker', selectedMarkerType.value);
  createAnomalyDialog.value = false;
}

const openCreateInspectionAreaDialog = () => { createInspectionDialog.value = true; }
const confirmCreateInspectionArea = () => {
  emits('createInspectionArea', newInspectionAreaName.value);
  createInspectionDialog.value = false;
}

const openCreateHotSpotDialog = () => { createHotSpotDialog.value = true; }
const confirmCreateHotSpot = () => {
  emits('onCreateHotSpot');
  createHotSpotDialog.value = false;
}

// Let a parent open the "choose anomaly type" dialog programmatically (used by the street-view
// polygon annotation flow, where there's no visible toolbar to click).
defineExpose({ openAnomalyDialog: openCreateAnomalyDialog });
</script>

<style scoped>
.sidebar-card {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  color: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Compact create header: a small caption + an inline "pick a point" hint, no heavy title. */
.create-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px 8px;
}

.create-eyebrow {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8aa0aa;
}

.create-hint {
  font-size: 0.66rem;
  color: #5f7684;
  white-space: nowrap;
}

.card-body {
  padding: 2px 12px 12px;
}

/* Create action grid */
.action-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}

.action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 7px 4px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  color: #cfd8dc;
  font: inherit;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.action-btn:not(:disabled):hover {
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
}

.action-btn.action-success:not(:disabled):hover {
  border-color: rgba(74, 222, 128, 0.4);
  color: #4ade80;
}

.action-btn.action-info:not(:disabled):hover {
  border-color: rgba(59, 130, 246, 0.4);
  color: #60a5fa;
}

.action-btn.action-danger:not(:disabled):hover {
  border-color: rgba(239, 68, 68, 0.4);
  color: #f87171;
}

/* Dialogs */
.dialog-card {
  border-radius: 14px !important;
  background: var(--duallo-card-gradient) !important;
  border: 1px solid rgba(255, 255, 255, 0.04);
  color: #fff;
}

.dialog-title {
  font-weight: 700;
  padding: 18px 20px 6px;
}

.primary-btn {
  background: var(--duallo-accent-gradient) !important;
  color: #071514 !important;
  font-weight: 700 !important;
  text-transform: none !important;
  border-radius: 10px !important;
}

.ghost-btn {
  background: rgba(255, 255, 255, 0.04) !important;
  color: #cfd8dc !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 10px !important;
}
</style>
